import React, { ReactNode } from "react";
import { UseFormRegister, FieldErrors, RegisterOptions } from "react-hook-form";

interface FormInputProps {
  label?: string | ReactNode;
  name: string;
  type?:
    | "text"
    | "email"
    | "password"
    | "select"
    | "textarea"
    | "tel"
    | "number"
    | "date";
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  options?: RegisterOptions;
  selectOptions?: any[];
  valueKey?: string;
  labelKey?: string;
  placeholder?: string;
  icon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
}

const FormInput = ({
  label,
  name,
  type = "text",
  register,
  errors,
  options = {},
  selectOptions = [],
  valueKey = "value",
  labelKey = "label",
  placeholder = "",
  icon,
  leftIcon,
  loading = false,
  disabled = false,
}: FormInputProps) => {
  const isDisabled = loading || disabled;
  const hasError = errors[name] && !isDisabled;

  // Base input classes with improved styling and RTL support
  const baseInputClasses = `
    w-full px-3 py-2.5 
    border border-gray-300 rounded-lg 
    bg-white text-gray-900 text-sm
    placeholder:text-gray-500
    transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
    hover:border-gray-400
    rtl:text-right ltr:text-left
    min-h-[44px] touch-manipulation
    shadow-sm hover:shadow-md focus:shadow-md
    ${
      hasError
        ? "border-red-500 focus:ring-red-500 focus:border-red-500 shadow-red-100"
        : ""
    }
    ${
      isDisabled
        ? "bg-gray-50 border-gray-200 cursor-not-allowed opacity-70 text-gray-500 shadow-none"
        : ""
    }
  `;

  // Special styling for date inputs
  const dateInputClasses = `
    ${baseInputClasses}
    cursor-pointer
    [&::-webkit-calendar-picker-indicator]:cursor-pointer
    [&::-webkit-calendar-picker-indicator]:opacity-60
    [&::-webkit-calendar-picker-indicator]:hover:opacity-100
    [&::-webkit-calendar-picker-indicator]:w-4
    [&::-webkit-calendar-picker-indicator]:h-4
    [&::-webkit-inner-spin-button]:appearance-none
    [&::-webkit-outer-spin-button]:appearance-none
    [&::-webkit-datetime-edit]:text-gray-700
    [&::-webkit-datetime-edit-fields-wrapper]:text-gray-700
    [&::-webkit-datetime-edit-text]:text-gray-400
    [&::-webkit-datetime-edit-month-field]:text-gray-900
    [&::-webkit-datetime-edit-day-field]:text-gray-900
    [&::-webkit-datetime-edit-year-field]:text-gray-900
    color-scheme: light
    background-color: white !important
  `;

  return (
    <div className="w-full space-y-1" dir="auto">
      {label && (
        <label
          htmlFor={name}
          className={`block text-sm font-medium text-gray-700 mb-1.5 rtl:text-right ltr:text-left ${
            isDisabled ? "opacity-70" : ""
          }`}
        >
          {label}
        </label>
      )}

      {type === "select" ? (
        <div className="relative">
          {leftIcon && (
            <div
              className={`absolute inset-y-0 ltr:left-0 rtl:right-0 flex items-center ltr:pl-3 rtl:pr-3 pointer-events-none z-10 ${
                isDisabled ? "opacity-70" : ""
              }`}
            >
              {leftIcon}
            </div>
          )}
          <select
            id={name}
            {...register(name, options)}
            className={`
              ${baseInputClasses}
              appearance-none cursor-pointer
              ltr:pr-10 rtl:pl-10
              ${leftIcon ? "ltr:pl-10 rtl:pr-10" : ""}
            `}
            disabled={isDisabled}
            style={{
              direction: "inherit",
            }}
          >
            <option value="" className="rtl:text-right ltr:text-left">
              {placeholder || "Select an option..."}
            </option>
            {selectOptions.map((option, index) => (
              <option
                key={index}
                value={typeof option === "object" ? option[valueKey] : option}
                className="rtl:text-right ltr:text-left rtl:direction-rtl ltr:direction-ltr"
              >
                {typeof option === "object" ? option[labelKey] : option}
              </option>
            ))}
          </select>
          {icon && (
            <div
              className={`absolute inset-y-0 ltr:right-0 rtl:left-0 flex items-center ltr:pr-3 rtl:pl-3 pointer-events-none ${
                isDisabled ? "opacity-70" : ""
              }`}
            >
              {icon}
            </div>
          )}
        </div>
      ) : type === "textarea" ? (
        <div className="relative">
          {leftIcon && (
            <div
              className={`absolute top-3 ltr:left-0 rtl:right-0 flex items-start ltr:pl-3 rtl:pr-3 pointer-events-none z-10 ${
                isDisabled ? "opacity-70" : ""
              }`}
            >
              {leftIcon}
            </div>
          )}
          <textarea
            id={name}
            {...register(name, options)}
            className={`
              ${baseInputClasses}
              resize-vertical min-h-[100px]
              ${leftIcon ? "ltr:pl-10 rtl:pr-10" : ""}
            `}
            placeholder={placeholder}
            rows={4}
            disabled={isDisabled}
          />
        </div>
      ) : (
        <div className="relative">
          {leftIcon && (
            <div
              className={`absolute inset-y-0 ltr:left-0 rtl:right-0 flex items-center ltr:pl-3 rtl:pr-3 pointer-events-none z-10 ${
                isDisabled ? "opacity-70" : ""
              }`}
            >
              {leftIcon}
            </div>
          )}
          <input
            id={name}
            type={type}
            {...register(name, options)}
            className={`
              ${type === "date" ? dateInputClasses : baseInputClasses}
              ${leftIcon ? "ltr:pl-10 rtl:pr-10" : ""}
            `}
            placeholder={type === "date" ? undefined : placeholder}
            min={type === "number" ? 1 : undefined}
            disabled={isDisabled}
            data-placeholder={type === "date" ? placeholder : undefined}
            style={
              type === "date"
                ? {
                    colorScheme: "light",
                    WebkitAppearance: "none",
                    MozAppearance: "textfield",
                  }
                : undefined
            }
          />
        </div>
      )}

      {hasError && (
        <p className="text-red-600 text-xs mt-1 rtl:text-right ltr:text-left">
          {errors[name]?.message?.toString()}
        </p>
      )}
    </div>
  );
};

export default FormInput;
